import { CheckCircledIcon, Cross2Icon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  MessageTemplatesService,
  ReachPerson,
  TemplateCatalog,
  TriggerKindInfo,
  TriggerReach,
  TriggerResponse,
} from "client/openapi";
import CrmColumnField, {
  catalogColumnChoice,
} from "components/Automations/CrmColumnField";
import { ButtonColor } from "components/Button";
import {
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { Tick } from "components/Forms/SendFormControls";
import NumberBox from "components/Inputs/NumberBox";
import { COMPACT_CHECK, COMPACT_INPUT } from "components/Inputs/compact";
import { LABEL } from "components/ProfileDropdown/settingsStyles";
import { Select, selectComponents } from "components/Select";
import { Tag } from "components/Tag/TagChip";
import { TagColor, TagSize } from "components/Tag";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  components,
  MenuProps,
  OptionProps,
  ValueContainerProps,
} from "react-select";
import Notifications from "util/notifications";
import { unsubscribedWords } from "./TemplatePreviewParts";

type Options = Record<string, any>;
type SendTo = TriggerResponse["send_to"][number];
export type Family = "students" | "parents" | "none";

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** How far apart a schedule's sends can be (the backend's clean_options). */
const EVERY_WEEKS_MAX = 8;
const EVERY_MONTHS_MAX = 12;

/** A new trigger's choices, by kind (see the backend's clean_options). */
export function defaultOptions(
  kind: string,
  catalog: TemplateCatalog
): Options {
  const form = Number(catalog.forms[0]?.value) || undefined;
  switch (kind) {
    case "cadence":
      return {
        frequency: "weekly",
        every_weeks: 1,
        weekday: 0,
        day_of_month: 1,
        time: "10:00",
        audience: "students",
        column: catalog.columns[0]?.value,
      };
    case "meeting_before":
      return { hours: 24 };
    case "meeting_after":
      return { hours: 2 };
    case "meeting_booked":
      return { first_only: false };
    case "meeting_missed":
      return { hours: 2 };
    case "first_meeting":
      return { days: 3 };
    case "milestone":
      return { count: 10 };
    case "no_meeting":
      return { days: 30 };
    case "form_filled":
      return { form_id: form, days: 0 };
    case "form_waiting":
      return { form_id: form, days: 3 };
    case "column_joined":
    case "column_left":
      return { column: catalog.columns[0]?.value };
    case "crm_date":
      return { field: catalog.date_fields[0]?.value, days: 0, yearly: false };
    case "charge_failed":
      return { days: 0 };
    case "balance_owed":
      return { days: 7, min_amount: 0 };
    case "no_payment_method":
      return { days: 3 };
    case "credit_low":
      return { below: 50 };
    default:
      return {};
  }
}

const ROLE_TAGS: Record<string, { label: string; color: TagColor }> = {
  [Auth0AccountRole.PARENT]: { label: "Parent", color: TagColor.BLUE },
  [Auth0AccountRole.ME]: { label: "Student", color: TagColor.GREEN },
  [Auth0AccountRole.ORG_TUTOR]: { label: "Tutor", color: TagColor.GOLD },
};

/**
 * Who the trigger's messages are about, when only one kind of person: the
 * audience of a cadence, a CRM column's people, a date field's people, a
 * tutors' form's tutors. Undefined when they are students and families.
 */
export function onlyKind(
  kind: string,
  options: Options,
  catalog: TemplateCatalog
): Auth0AccountRole | undefined {
  const columnType = (value?: string) =>
    catalog.columns.find((c) => c.value === value)?.person_type ?? undefined;
  if (kind === "cadence") {
    if (options.audience === "parents") return Auth0AccountRole.PARENT;
    if (options.audience === "tutors") return Auth0AccountRole.ORG_TUTOR;
    if (options.audience === "column") {
      const type = columnType(options.column);
      return type === Auth0AccountRole.ME ? undefined : type;
    }
    return undefined;
  }
  if (kind === "column_joined" || kind === "column_left") {
    const type = columnType(options.column);
    return type === Auth0AccountRole.ME ? undefined : type;
  }
  if (kind === "crm_date") {
    const type = catalog.date_fields.find(
      (f) => f.value === options.field
    )?.person_type;
    return type === Auth0AccountRole.ME ? undefined : type ?? undefined;
  }
  if (kind === "form_filled" || kind === "form_waiting") {
    // A tutors' form is filled out by tutors; a families' form by families.
    const type = catalog.forms.find(
      (f) => f.value === String(options.form_id)
    )?.person_type;
    return type === Auth0AccountRole.ORG_TUTOR ? type : undefined;
  }
  return undefined;
}

/** The send_to the server stores, from the family choice and the tutor tick. */
export function sendToFor(
  only: Auth0AccountRole | undefined,
  family: Family,
  tutors: boolean
): SendTo[] {
  if (only === Auth0AccountRole.PARENT) return ["parents"];
  if (only === Auth0AccountRole.ORG_TUTOR) return ["tutors"];
  const chosen: SendTo[] = family === "none" ? [] : [family];
  return tutors ? [...chosen, "tutors"] : chosen;
}

/** A labeled row of a trigger's choices. */
/** A choice of a few, as Tadpole's pills (like Email and Text). */
function Pills<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: [T, string][];
}) {
  return (
    <div
      className="inline-flex w-fit flex-wrap rounded-full border border-[#98acfc] p-0.5"
      role="group"
      aria-label={label}
    >
      {options.map(([option, text]) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          className={`rounded-full px-3 py-1 text-xs transition-colors duration-200 ${
            value === option
              ? "bg-[#98acfc] text-white"
              : "text-[#98acfc] hover:bg-[#98acfc]/10"
          }`}
          onClick={() => onChange(option)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-3">
      <p className={LABEL}>{label}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
        {children}
      </div>
    </div>
  );
}

/**
 * The room from a dropdown's left edge to the edge of what would cut it
 * off: the scrolling fields of a dialog, say, else the window.
 */
function roomBeside(menu: HTMLElement) {
  const { left } = menu.getBoundingClientRect();
  for (let el = menu.parentElement; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowX !== "visible")
      return (
        el.getBoundingClientRect().right -
        parseFloat(style.paddingRight) -
        parseFloat(style.borderRightWidth) -
        left
      );
  }
  return window.innerWidth - 16 - left;
}

/**
 * A dropdown's list: as wide as its widest choice, never narrower than its
 * box, and never wider than the room beside it, so nothing cuts it off.
 */
function FitMenu<Option, IsMulti extends boolean>(
  props: MenuProps<Option, IsMulti>
) {
  const { innerRef, innerProps } = props;
  const [room, setRoom] = useState<number>();
  const measured = useCallback(
    (menu: HTMLDivElement | null) => {
      // The Select places its list through this ref, always a function.
      if (typeof innerRef === "function") innerRef(menu);
      if (menu) setRoom(roomBeside(menu));
    },
    [innerRef]
  );
  return (
    <components.Menu
      {...props}
      innerRef={measured}
      innerProps={{
        ...innerProps,
        style:
          room === undefined
            ? undefined
            : { maxWidth: `min(24rem, ${room}px)` },
      }}
      className={`${
        props.className ?? ""
      } !w-max min-w-full max-w-[min(24rem,calc(100vw-2rem))]`}
    />
  );
}

/**
 * A choice in that list: its words stay on one line, cut with "…" and whole
 * on hover when the list can't hold them, and it is gray while it is off.
 */
function FitOption<Option, IsMulti extends boolean>(
  props: OptionProps<Option, IsMulti>
) {
  const { children, isDisabled, isSelected } = props;
  return (
    <components.Option {...props}>
      <div
        className={`flex flex-row items-center gap-2 ${
          isDisabled ? "text-gray-400" : ""
        }`}
      >
        {typeof children === "string" ? (
          <span className="min-w-0 truncate" title={children}>
            {children}
          </span>
        ) : (
          children
        )}
        {isSelected && <CheckCircledIcon className="flex-none text-purple" />}
      </div>
    </components.Option>
  );
}

/** The app's Select's own parts, with the list that fits its choices. */
export const fitComponents = {
  ...selectComponents,
  Menu: FitMenu,
  Option: FitOption,
};

type ChoiceOption = { value: string | number; label: string };

/**
 * A dropdown's value, with each of its choices' words laid in the same
 * place, hidden: the box is as wide as its widest choice, whichever is
 * picked.
 */
function FitValueContainer(props: ValueContainerProps<ChoiceOption, false>) {
  return (
    <components.ValueContainer {...props}>
      {props.children}
      {(props.selectProps.options as ChoiceOption[]).map((o) => (
        <span
          key={String(o.value)}
          aria-hidden
          className="invisible h-0 overflow-hidden whitespace-nowrap pl-2.5 [grid-area:1/1/2/3]"
        >
          {o.label}
        </span>
      ))}
    </components.ValueContainer>
  );
}

/**
 * A dropdown of a trigger's choices, as wide as its widest choice. A Select
 * opens on its value only when the value is one of its options (the same
 * object), so the value is found among them; values compare as text, as
 * the stored ones may be numbers.
 */
function Choice({
  value,
  onChange,
  options,
  label,
}: {
  value: string | number | undefined;
  onChange: (value: string) => void;
  options: ChoiceOption[];
  label: string;
}) {
  return (
    <div className="max-w-xs">
      <Select
        aria-label={label}
        isSearchable={false}
        menuPlacement="auto"
        options={options}
        value={options.find((o) => String(o.value) === String(value)) ?? null}
        onChange={(o: { value: string | number }) => onChange(String(o.value))}
        components={{ ...fitComponents, ValueContainer: FitValueContainer }}
      />
    </div>
  );
}

/** Hours, shown in days when they are whole days. */
function HoursBox({
  hours,
  onChange,
}: {
  hours: number;
  onChange: (hours: number) => void;
}) {
  const [unit, setUnit] = useState(
    hours % 24 === 0 ? "days" : ("hours" as "days" | "hours")
  );
  const shown = unit === "days" ? hours / 24 : hours;
  return (
    <>
      <NumberBox
        label={unit === "days" ? "Days" : "Hours"}
        value={shown}
        min={1}
        max={unit === "days" ? 14 : 336}
        onChange={(n) => onChange(unit === "days" ? n * 24 : n)}
      />
      <Choice
        label="Unit"
        value={unit}
        onChange={(next) => {
          setUnit(next as "days" | "hours");
          onChange(
            next === "days" ? Math.max(1, Math.round(hours / 24)) * 24 : hours
          );
        }}
        options={[
          { value: "hours", label: "hours" },
          { value: "days", label: "days" },
        ]}
      />
    </>
  );
}

/**
 * The choices of one kind of trigger. timing false leaves out the kind's
 * own "days after" or "hours before" (an automation's first steps carry
 * it), keeping only what picks the event: the form, column, date, schedule
 * or amount.
 */
export function KindOptions({
  kind,
  options,
  set,
  catalog,
  timing = true,
}: {
  kind: string;
  options: Options;
  set: (changes: Options) => void;
  catalog: TemplateCatalog;
  timing?: boolean;
}) {
  const forms = catalog.forms.map((f) => ({ value: f.value, label: f.label }));
  const noForms = forms.length === 0 && (
    <p className="text-sm text-red-600">
      Make a form first, on the Forms page.
    </p>
  );
  // The CRM column: its visualization, then one of its columns.
  const column = (
    <CrmColumnField
      columns={catalog.columns.map(catalogColumnChoice)}
      value={options.column}
      onChange={(key) => set({ column: key })}
    />
  );
  const days = (label: string, min: number, max: number, after: string) =>
    timing ? (
      <Row label={label}>
        <NumberBox
          label={label}
          value={options.days}
          min={min}
          max={max}
          onChange={(n) => set({ days: n })}
        />
        {after}
      </Row>
    ) : null;

  switch (kind) {
    case "cadence": {
      // "Every [2] [week(s)]" or "Every [3] [month(s)]": the number keeps
      // to each unit's limits (the backend's), and carries over a switch.
      const monthly = options.frequency === "monthly";
      const every = Number(
        monthly ? options.every_months : options.every_weeks
      );
      const within = (most: number) =>
        Math.min(most, Math.max(1, Number.isFinite(every) ? every : 1));
      return (
        <>
          <Row label="How often">
            Every
            <NumberBox
              label={monthly ? "How many months" : "How many weeks"}
              value={monthly ? options.every_months ?? 1 : options.every_weeks}
              min={1}
              max={monthly ? EVERY_MONTHS_MAX : EVERY_WEEKS_MAX}
              narrow
              onChange={(n) =>
                set(monthly ? { every_months: n } : { every_weeks: n })
              }
            />
            <Choice
              label="Weeks or months"
              value={monthly ? "monthly" : "weekly"}
              onChange={(value) =>
                value === "monthly"
                  ? set({
                      frequency: "monthly",
                      every_months: within(EVERY_MONTHS_MAX),
                      day_of_month: options.day_of_month ?? 1,
                    })
                  : set({
                      frequency: "weekly",
                      every_weeks: within(EVERY_WEEKS_MAX),
                      weekday: options.weekday ?? 0,
                    })
              }
              options={[
                { value: "weekly", label: "week(s)" },
                { value: "monthly", label: "month(s)" },
              ]}
            />
            {monthly ? (
              <>
                on the
                <Choice
                  label="Day of the month"
                  value={options.day_of_month}
                  onChange={(value) => set({ day_of_month: Number(value) })}
                  options={Array.from({ length: 28 }, (_, i) => ({
                    value: i + 1,
                    label: ordinal(i + 1),
                  }))}
                />
              </>
            ) : (
              <>
                on
                <Choice
                  label="Day of the week"
                  value={options.weekday}
                  onChange={(value) => set({ weekday: Number(value) })}
                  options={WEEKDAYS.map((day, i) => ({ value: i, label: day }))}
                />
              </>
            )}
            at
            <input
              type="time"
              aria-label="Time"
              step={900}
              className={COMPACT_INPUT}
              value={options.time ?? "10:00"}
              onChange={(e) => set({ time: e.target.value })}
            />
          </Row>
          <Row label="To">
            <Choice
              label="To"
              value={options.audience}
              onChange={(value) => set({ audience: value })}
              options={[
                { value: "students", label: "All active students" },
                { value: "parents", label: "All active parents" },
                { value: "tutors", label: "All active tutors" },
                { value: "column", label: "The people in a CRM column" },
              ]}
            />
          </Row>
          {options.audience === "column" && column}
        </>
      );
    }
    case "meeting_before":
    case "meeting_after":
    case "meeting_missed":
      if (!timing) return null;
      return (
        <Row label="When">
          <HoursBox
            hours={options.hours ?? 24}
            onChange={(hours) => set({ hours })}
          />
          {kind === "meeting_before"
            ? "before each meeting starts"
            : "after each meeting ends"}
        </Row>
      );
    case "meeting_booked":
      return (
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className={COMPACT_CHECK}
            checked={!!options.first_only}
            onChange={(e) => set({ first_only: e.target.checked })}
          />
          Only their first booking
        </label>
      );
    case "first_meeting":
      return days(
        "Days after their first meeting",
        0,
        365,
        "days after (0 is the same day)"
      );
    case "milestone":
      return (
        <Row label="After this many meetings">
          <NumberBox
            label="Meetings"
            value={options.count}
            min={1}
            max={1000}
            onChange={(n) => set({ count: n })}
          />
          meetings attended
        </Row>
      );
    case "no_meeting":
      return days(
        "Days with no meeting",
        1,
        365,
        "days since their last meeting, with none booked"
      );
    case "form_filled":
    case "form_waiting":
      return (
        <>
          <Row label="Form">
            {noForms || (
              <Choice
                label="Form"
                value={options.form_id}
                onChange={(value) => set({ form_id: Number(value) })}
                options={forms}
              />
            )}
          </Row>
          {kind === "form_filled"
            ? days(
                "Days after it is filled out",
                0,
                90,
                "days after (0 is right away)"
              )
            : days(
                "Days after it was sent",
                1,
                13,
                "days after it was sent, if it is still not filled out"
              )}
        </>
      );
    case "column_joined":
    case "column_left":
      return column;
    case "crm_date": {
      const direction =
        (options.days ?? 0) < 0 ? "before" : options.days > 0 ? "after" : "on";
      return (
        <>
          <Row label="Date">
            <Choice
              label="Date field"
              value={options.field}
              onChange={(value) => set({ field: value })}
              options={catalog.date_fields.map((f) => ({
                value: f.value,
                label: f.label,
              }))}
            />
          </Row>
          {timing && (
            <Row label="When">
              {direction !== "on" && (
                <NumberBox
                  label="Days"
                  value={Math.abs(options.days ?? 0)}
                  min={1}
                  max={90}
                  onChange={(n) =>
                    set({ days: direction === "before" ? -n : n })
                  }
                />
              )}
              <Choice
                label="Before, on, or after"
                value={direction}
                onChange={(value) => {
                  const amount = Math.abs(options.days ?? 0) || 1;
                  set({
                    days:
                      value === "on"
                        ? 0
                        : value === "before"
                        ? -amount
                        : amount,
                  });
                }}
                options={[
                  { value: "before", label: "days before" },
                  { value: "on", label: "on the day" },
                  { value: "after", label: "days after" },
                ]}
              />
            </Row>
          )}
          {!String(options.field ?? "").endsWith(".birth_date") && (
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className={COMPACT_CHECK}
                checked={!!options.yearly}
                onChange={(e) => set({ yearly: e.target.checked })}
              />
              Every year (like an anniversary)
            </label>
          )}
        </>
      );
    }
    case "charge_failed":
      return days(
        "Days after the charge fails",
        0,
        60,
        "days after (0 is right away), if they still owe"
      );
    case "balance_owed":
      return (
        <>
          {days("Days owed", 1, 180, "days in a row")}
          <Row label="When they owe more than">
            $
            <NumberBox
              label="Dollars"
              value={options.min_amount}
              min={0}
              max={100000}
              step={0.01}
              onChange={(n) => set({ min_amount: n })}
            />
          </Row>
        </>
      );
    case "no_payment_method":
      return days(
        "Days with no card or bank",
        0,
        180,
        "days with no card or bank, from when they last had one or joined"
      );
    case "credit_low":
      return (
        <Row label="When prepaid credit drops below">
          $
          <NumberBox
            label="Dollars"
            value={options.below}
            min={1}
            max={100000}
            step={0.01}
            onChange={(n) => set({ below: n })}
          />
        </Row>
      );
    default:
      return null;
  }
}

function ordinal(n: number) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

/**
 * Add a trigger to a template, or change one that is off: when it sends,
 * who gets it, how, and whether each message waits for an admin's
 * approval. A new trigger is on unless the admin switches it off, and the
 * dialog shows who it reaches before it is added (the server checks that
 * this is still so when it adds it). A promotional kind of trigger on an
 * operational template starts off and asking first. One added on is
 * handed to onSaved with added, so the template's page can offer Undo on
 * its row. Mount it inside a Dialog.
 */
export function TriggerDialog({
  templateId,
  catalog,
  versions,
  trigger,
  promotional,
  onMakePromotional,
  onSaved,
  onCloseAutoFocus,
}: {
  templateId: number;
  catalog: TemplateCatalog;
  /** The versions the template has: the ways a trigger can send it. */
  versions: { sms: boolean; email: boolean };
  /** The trigger to change; none to add one. */
  trigger?: TriggerResponse;
  /** Whether the template is promotional. */
  promotional: boolean;
  /** Make the template promotional (only when the admin asks). */
  onMakePromotional: () => void;
  /**
   * The trigger as saved; added when it was just added and is on (no toast
   * then: the page shows Undo on its row), with whether those who already
   * match get it once.
   */
  onSaved: (trigger: TriggerResponse, added?: { caughtUp: boolean }) => void;
  /** Where focus goes when the dialog closes (preventDefault to move it). */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const [kind, setKind] = useState<string | undefined>(trigger?.kind);
  // A trigger's saved choices keep only what its kind uses now (a weekly
  // cadence has no day of the month): the rest start from the defaults.
  const [options, setOptions] = useState<Options>(
    trigger
      ? { ...defaultOptions(trigger.kind, catalog), ...trigger.options }
      : {}
  );
  const sendTo = trigger?.send_to ?? ["students"];
  const [family, setFamily] = useState<Family>(
    sendTo.includes("students")
      ? "students"
      : sendTo.includes("parents")
      ? "parents"
      : "none"
  );
  const [tutors, setTutors] = useState(sendTo.includes("tutors"));
  const [sms, setSms] = useState(
    trigger?.sms ?? (versions.sms && !versions.email)
  );
  const [email, setEmail] = useState(trigger?.email ?? versions.email);
  const [needsApproval, setNeedsApproval] = useState(
    trigger?.needs_approval ?? false
  );
  // A new trigger is on. On and Ask me are switched on its row.
  const [turnOn, setTurnOn] = useState(true);
  const [catchUp, setCatchUp] = useState(false);
  const [saving, setSaving] = useState(false);
  // Why the last Add or Save didn't go through ("This template already has
  // this trigger."), shown by the buttons until a choice changes.
  const [saveError, setSaveError] = useState<string>();

  const info = catalog.trigger_kinds.find((k) => k.kind === kind);
  const only = kind ? onlyKind(kind, options, catalog) : undefined;
  const chosenSendTo = sendToFor(only, family, tutors);
  // Reads like marketing, on a template that has no unsubscribe link yet.
  const cautious = !!info?.promotional && !promotional;

  // A new trigger's switches: on and sending on its own, or, for a
  // promotional kind on an operational template, off and asking first.
  useEffect(() => {
    if (trigger || !kind) return;
    setTurnOn(!cautious);
    setNeedsApproval(cautious);
  }, [trigger, kind, cautious]);

  useEffect(
    () => setSaveError(undefined),
    [kind, options, family, tutors, sms, email, needsApproval, turnOn, catchUp]
  );

  // Who it reaches with these choices, shown under Who gets it.
  const draft =
    kind && chosenSendTo.length > 0 && (sms || email)
      ? { kind, options, send_to: chosenSendTo, sms, email }
      : null;
  const {
    reach,
    error: reachError,
    loading,
    reload,
  } = useDraftReach(templateId, draft);
  const already = reach?.already?.count ? reach.already : undefined;

  function pickKind(next: string) {
    setKind(next);
    setOptions(defaultOptions(next, catalog));
    setCatchUp(false);
  }

  function save() {
    if (!kind) return;
    setSaving(true);
    setSaveError(undefined);
    const body = {
      options,
      send_to: chosenSendTo,
      sms,
      email,
      needs_approval: needsApproval,
    };
    const caughtUp = turnOn && catchUp && !!already;
    (trigger
      ? MessageTemplatesService.updateMessageTemplateTrigger({
          triggerId: trigger.id,
          requestBody: body,
        })
      : MessageTemplatesService.createMessageTemplateTrigger({
          templateId,
          requestBody: {
            kind,
            ...body,
            enabled: turnOn,
            seen_count: turnOn ? reach?.count : undefined,
            catch_up: caughtUp,
            seen_catch_up_count: caughtUp ? already?.count : undefined,
          },
        })
    )
      .then((saved) => {
        if (!trigger && saved.enabled) {
          onSaved(saved, { caughtUp });
          return;
        }
        onSaved(saved);
        Notifications.success(
          trigger
            ? "The trigger was saved."
            : "The trigger was added. It stays off until you turn it on."
        );
      })
      .catch((e) => {
        const reason = errorText(e, "The trigger could not be saved.");
        setSaveError(reason);
        // Who it reaches changed: look again before adding it.
        if (reason.includes("changed")) reload();
      })
      .finally(() => setSaving(false));
  }

  // A new trigger's X goes back from its choices to the kinds of trigger.
  const backToKinds = !trigger && !!kind;

  return (
    <DialogContent
      className="dialog-content max-w-[880px]"
      showClose={!backToKinds}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {backToKinds && (
        <button
          type="button"
          className="dialog-close"
          aria-label="Back to the list of triggers"
          onClick={() => setKind(undefined)}
        >
          <Cross2Icon width={15} height={15} />
        </button>
      )}
      <h2 className="text-xl font-semibold">
        {trigger ? "Change the trigger" : "Add an automatic trigger"}
      </h2>

      {!kind ? (
        <>
          <p className="mt-1 text-sm text-gray-600">
            Pick when this template sends itself.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {catalog.trigger_kinds.map((k) => (
              <button
                key={k.kind}
                type="button"
                onClick={() => pickKind(k.kind)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-left hover:border-[#98acfc] hover:bg-[#98acfc]/5"
              >
                <span className="block text-sm font-semibold text-gray-900">
                  {k.label}
                </span>
                <span className="block text-xs text-gray-600">
                  {k.description}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{info?.label}.</span>{" "}
            {info?.description}
          </p>
          {cautious && (
            <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">
              Messages like this are usually promotional.{" "}
              <button
                type="button"
                className="font-medium underline"
                onClick={onMakePromotional}
              >
                Make this template Promotional
              </button>{" "}
              so its emails have an unsubscribe link.
              {!trigger &&
                " Until then, a new trigger like this starts off and asks you before each one goes out."}
            </p>
          )}

          <KindOptions
            kind={kind}
            options={options}
            set={(changes) => setOptions((o) => ({ ...o, ...changes }))}
            catalog={catalog}
          />

          <Row label="Who gets it">
            {only === Auth0AccountRole.PARENT ? (
              <span>The parents.</span>
            ) : only === Auth0AccountRole.ORG_TUTOR ? (
              <span>The tutors.</span>
            ) : (
              <div className="flex flex-col gap-2">
                <Pills
                  label="Who gets it"
                  value={family}
                  onChange={(next) => {
                    // Tutors only: the tutors; else the family only.
                    setTutors(next === "none");
                    setFamily(next);
                  }}
                  options={[
                    ["students", "Student and parents"],
                    ["parents", "Parents only"],
                    ["none", "Tutors only"],
                  ]}
                />
                {draft && reachError && (
                  <p className="mt-2 text-sm text-red-600">{reachError}</p>
                )}
                {draft && reach && (
                  <div className={`mt-2 ${loading ? "opacity-60" : ""}`}>
                    <ReachDetails
                      reach={reach}
                      kind={kind}
                      needsApproval={needsApproval}
                      catchUp={catchUp}
                      onCatchUp={setCatchUp}
                      showCatchUp={!trigger}
                    />
                  </div>
                )}
                {info?.about !== TriggerKindInfo.about.STUDENTS && (
                  <p className="text-xs text-gray-500">
                    When it is about a parent, the parent gets it. When it is
                    about a tutor, the tutor gets it.
                  </p>
                )}
              </div>
            )}
          </Row>

          <Row label="Send by">
            {versions.sms && (
              <Tick checked={sms} onChange={setSms}>
                SMS
              </Tick>
            )}
            {versions.email && (
              <Tick checked={email} onChange={setEmail}>
                Email
              </Tick>
            )}
            {!versions.sms && !versions.email && (
              <span className="text-red-600">
                Write the email or the text first.
              </span>
            )}
          </Row>
        </>
      )}

      {saveError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {saveError}
        </p>
      )}
      <DialogActions>
        {kind && (
          <DialogAction
            primary={true}
            color={ButtonColor.GREEN}
            disabled={
              saving ||
              (!sms && !email) ||
              chosenSendTo.length === 0 ||
              (!!draft && (!reach || loading))
            }
            onClick={save}
          >
            {trigger ? "Save" : "Add trigger"}
          </DialogAction>
        )}
        <DialogClose asChild>
          <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
        </DialogClose>
      </DialogActions>
    </DialogContent>
  );
}

/**
 * Who a trigger with these choices would reach if it were on now, from the
 * server, about 400 ms after the choices last changed. An answer to older
 * choices is dropped. Nothing is asked while draft is null.
 */
function useDraftReach(
  templateId: number,
  draft: {
    kind: string;
    options: Options;
    send_to: SendTo[];
    sms: boolean;
    email: boolean;
  } | null
) {
  const [reach, setReach] = useState<TriggerReach>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [again, setAgain] = useState(0);
  const key = draft ? JSON.stringify(draft) : null;
  const asked = useRef(0);

  useEffect(() => {
    const ask = ++asked.current;
    if (key === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      MessageTemplatesService.getMessageTemplateDraftTriggerReach({
        templateId,
        requestBody: JSON.parse(key),
      })
        .then((answer) => {
          if (ask !== asked.current) return;
          setReach(answer);
          setError(undefined);
        })
        .catch((e) => {
          if (ask !== asked.current) return;
          setReach(undefined);
          setError(errorText(e, "Who it reaches could not be loaded."));
        })
        .finally(() => ask === asked.current && setLoading(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [templateId, key, again]);

  return {
    reach: key === null ? undefined : reach,
    error: key === null ? undefined : error,
    loading,
    reload: () => setAgain((n) => n + 1),
  };
}

/** How many people a trigger would message, in words. */
function reachWords(kind: string, count: number) {
  const people = count === 1 ? "1 person" : `${count} people`;
  if (kind === "cadence")
    return count === 0
      ? "Nobody would get its next message."
      : `Its next message would go to ${people}.`;
  return count === 0
    ? "Nobody new would get it in the next day."
    : `In the next day, it would message ${people}.`;
}

/**
 * The people a reach names, with why, blank details and unsubscribes. With
 * onPick, clicking someone picks them (picked is highlighted).
 */
function ReachPeople({
  people,
  count,
  picked,
  onPick,
}: {
  people: ReachPerson[];
  count: number;
  picked?: (person: ReachPerson) => boolean;
  onPick?: (person: ReachPerson) => void;
}) {
  if (people.length === 0) return null;
  return (
    <ul className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white text-sm">
      {people.map((person) => {
        const tag = ROLE_TAGS[person.person_type];
        const isPicked = !!picked?.(person);
        return (
          <li
            key={`${person.person_type}-${person.person_id}-${person.detail}`}
            className={`flex flex-wrap items-center gap-2 border-b border-gray-100 px-3 py-1.5 last:border-0 ${
              onPick ? "cursor-pointer hover:bg-[#98acfc]/10" : ""
            } ${isPicked ? "bg-[#98acfc]/15" : ""}`}
            onClick={onPick ? () => onPick(person) : undefined}
            onKeyDown={
              onPick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onPick(person);
                    }
                  }
                : undefined
            }
            role={onPick ? "button" : undefined}
            tabIndex={onPick ? 0 : undefined}
            aria-pressed={onPick ? isPicked : undefined}
          >
            <span className="font-medium text-gray-900">{person.name}</span>
            {tag && (
              <Tag item={tag.label} color={tag.color} size={TagSize.MINI} />
            )}
            {person.detail && (
              <span className="text-gray-500">{person.detail}</span>
            )}
            {person.unsubscribed && (
              <span className="text-gray-500">
                Unsubscribed from promotional emails
              </span>
            )}
            {!!person.blank?.length && (
              <span className="basis-full text-xs text-amber-700">
                Blank: {person.blank.join(", ")}. Their message waits for your
                approval.
              </span>
            )}
          </li>
        );
      })}
      {count > people.length && (
        <li className="px-3 py-1.5 text-gray-500">
          And {count - people.length} more.
        </li>
      )}
    </ul>
  );
}

/** Who already matches, by name: "Noah Kim already matches." */
function alreadyWords(count: number, people: ReachPerson[]) {
  const names = people.slice(0, 3).map((person) => person.name);
  const more = count - names.length;
  const list =
    more > 0
      ? `${names.join(", ")}, and ${more} more`
      : names.length > 1
      ? `${names.slice(0, -1).join(", ")}${names.length > 2 ? "," : ""} and ${
          names[names.length - 1]
        }`
      : names[0] ?? `${count} people`;
  return `${list} already ${count === 1 ? "matches" : "match"}.`;
}

/**
 * Who a trigger reaches if it is on now: how many in the next day (a
 * cadence: its next send), when it checks, its note, whether each waits
 * for approval, and who unsubscribed. For a trigger that watches for a
 * state, how many already match, with a box (unticked) to send to them once
 * now. The template's page lists the people and shows their message.
 */
function ReachDetails({
  reach,
  kind,
  needsApproval,
  catchUp,
  onCatchUp,
  showCatchUp = true,
}: {
  reach: TriggerReach;
  kind: string;
  needsApproval: boolean;
  catchUp: boolean;
  onCatchUp: (catchUp: boolean) => void;
  /** Offer to send to those who already match (only when adding or turning on). */
  showCatchUp?: boolean;
}) {
  const already =
    showCatchUp && reach.already?.count ? reach.already : undefined;
  const timing = [
    reach.when,
    needsApproval
      ? "Each one waits for your approval."
      : "Each one goes out on its own.",
    reach.unsubscribed ? unsubscribedWords(reach.unsubscribed) : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <>
      <p className="text-sm text-gray-700">
        <span className="font-semibold text-gray-900">
          {reachWords(kind, reach.count)}
        </span>{" "}
        {timing}
      </p>
      {reach.note && (
        <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">
          {reach.note}
        </p>
      )}
      <ReachPeople people={reach.people} count={reach.count} />
      {already && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">
              {alreadyWords(already.count, already.people)}
            </span>{" "}
            It only messages people as they newly match, unless you send it to
            these now.
          </p>
          <label className="mt-2 flex items-center gap-2 text-sm text-gray-900">
            <input
              type="checkbox"
              className={COMPACT_CHECK}
              checked={catchUp}
              onChange={(e) => onCatchUp(e.target.checked)}
            />
            Send it to them now too
          </label>
        </div>
      )}
    </>
  );
}

/**
 * Before a trigger is turned on: who it would message now, and one of
 * their messages. Turning it on sends the counts shown, so the server
 * refuses if who it reaches changed since (then this looks again). For a
 * trigger that watches for a state, it also shows who already matches,
 * with a box (unticked) to send to them once now. Mount it inside a Dialog.
 */
export function ReachDialog({
  trigger,
  onEnabled,
}: {
  trigger: TriggerResponse;
  onEnabled: (trigger: TriggerResponse) => void;
}) {
  const [reach, setReach] = useState<TriggerReach>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [catchUp, setCatchUp] = useState(false);

  const load = useCallback(() => {
    setReach(undefined);
    setCatchUp(false);
    MessageTemplatesService.getMessageTemplateTriggerReach({
      triggerId: trigger.id,
    })
      .then(setReach)
      .catch((e) =>
        setError(errorText(e, "Who it reaches could not be loaded."))
      );
  }, [trigger.id]);

  useEffect(load, [load]);

  const already = reach?.already?.count ? reach.already : undefined;

  function turnOn() {
    if (!reach) return;
    setBusy(true);
    MessageTemplatesService.enableMessageTemplateTrigger({
      triggerId: trigger.id,
      requestBody: {
        seen_count: reach.count,
        catch_up: catchUp && !!already,
        seen_catch_up_count: already?.count ?? null,
      },
    })
      .then((enabled) => {
        Notifications.success(
          catchUp && already
            ? "The trigger is on, and those who already match will get it once."
            : "The trigger is on."
        );
        onEnabled(enabled);
      })
      .catch((e) => {
        const reason = errorText(e, "The trigger could not be turned on.");
        Notifications.error(reason);
        // Who it reaches changed: look again before trying once more.
        if (reason.includes("changed")) load();
      })
      .finally(() => setBusy(false));
  }

  return (
    <DialogContent className="dialog-content max-w-[880px]">
      <h2 className="text-xl font-semibold">Turn on this trigger?</h2>
      <p className="mt-1 text-sm text-gray-700">{trigger.label}.</p>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {!reach && !error && (
        <div className="mt-4 animate-pulse space-y-3">
          <div className="h-10 rounded bg-gray-200" />
          <div className="h-40 rounded bg-gray-100" />
        </div>
      )}

      {reach && (
        <div className="mt-4">
          <ReachDetails
            reach={reach}
            kind={trigger.kind}
            needsApproval={trigger.needs_approval}
            catchUp={catchUp}
            onCatchUp={setCatchUp}
          />
        </div>
      )}

      <DialogActions>
        <DialogAction
          primary={true}
          color={ButtonColor.GREEN}
          disabled={!reach || busy}
          onClick={turnOn}
        >
          Turn it on
        </DialogAction>
        <DialogClose asChild>
          <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
        </DialogClose>
      </DialogActions>
    </DialogContent>
  );
}
